/**
 * Sound service is a global api to handle sound related aspects.
 * It can play sound files, toggle sound settings, convert text to speech and convert speech to text.
 * It also provides a soundex algorithm to convert words to soundex codes.
 * Soundex is a phonetic algorithm for indexing names by sound, as pronounced in English.
 * Sound files are stored in the assets/sounds folder and must be in mp3 format.
 * Text to speech and speech to text are browser based and may not be supported by all browsers.
 *
 */

import {
  AbstractMesh,
  AudioParameterRampShape,
  CreateSoundAsync,
  IStaticSoundOptions,
  IStaticSoundPlayOptions,
  IStaticSoundStopOptions,
  SoundState,
  StaticSound,
} from '@babylonjs/core';
import { AssetFactory } from './AssetFactory';
import { Game } from './Game';

export class SoundService {
  static soundexDic = {
    A: 0,
    B: 1,
    C: 2,
    D: 3,
    E: 0,
    F: 1,
    G: 2,
    H: 0,
    I: 0,
    J: 2,
    K: 2,
    L: 4,
    M: 5,
    N: 5,
    O: 0,
    P: 1,
    Q: 2,
    R: 6,
    S: 2,
    T: 3,
    U: 0,
    V: 1,
    W: 0,
    X: 2,
    Y: 0,
    Z: 2,
  };

  sounds: { [key: string]: StaticSound } = {};
  private voicesReady: Promise<SpeechSynthesisVoice[]>;
  private voice: SpeechSynthesisVoice;

  constructor(private game: Game) {
    // Pre-load voices to ensure they're available on first textToSpeech call
    this.voicesReady = this.loadVoices();
  }

  private loadVoices(): Promise<SpeechSynthesisVoice[]> {
    return new Promise((resolve) => {
      if (!('speechSynthesis' in window)) {
        resolve([]);
        return;
      }

      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        this.voice = voices.find((voice) => voice.lang === 'en-US' && voice.name.includes('Google'));
        resolve(voices);
        return;
      }

      // Voices not loaded yet, wait for the voiceschanged event
      window.speechSynthesis.onvoiceschanged = () => {
        resolve(window.speechSynthesis.getVoices());
      };
    });
  }

  async createBackgroundMusic(filename: string): Promise<StaticSound> {
    if (this.game.config.mute || !this.game.user.settings.enableMusic) {
      return;
    }
    return await this.create('bgMusic', filename, 'music', { autoplay: true, loop: true, volume: this.game.user.settings.volumeMusic });
  }

  stopBackgroundMusic() {
    if (this.game.config.mute || !this.game.user.settings.enableMusic) {
      return;
    }
    this.fadeOut('bgMusic');
  }

  pauseBackgroundMusic() {
    if (this.game.config.mute || !this.game.user.settings.enableMusic) {
      return;
    }
    const sound = this.getSound('bgMusic');
    if (!sound) {
      return;
    }
    if (sound.state !== SoundState.Paused) {
      sound.pause();
    } else {
      sound.play();
    }
  }

  async create(name: string, filename: string, type: 'music' | 'fx', options?: Partial<IStaticSoundOptions>): Promise<StaticSound> {
    if (this.game.config.mute) {
      return;
    }
    if (!this.game.user.settings.enableMusic && type === 'music') {
      return;
    }
    if (!this.game.user.settings.enableSound && type === 'fx') {
      return;
    }

    const basePath = 'assets/';
    const filePath = 'sound/' + type + '/' + filename;
    const path = basePath + filePath;
    const extension = AssetFactory.getManifestFileExtension(filePath);

    let volume = this.game.user.settings.volumeSound;
    if (type === 'music') {
      volume = this.game.user.settings.volumeMusic;
    }
    if (options?.volume) {
      volume = options.volume * volume;
      options.volume = volume;
    }

    const sound = await CreateSoundAsync(
      name,
      path + extension,
      options || {
        volume: volume,
      }
    );
    this.sounds[name] = sound;
    return sound;
  }

  /**
   * Creates a spatial sound for the given mesh.
   * The sound must be created before using create method.
   * The sound will be cloned for each mesh to allow multiple sounds to play simultaneously.
   *
   * @param name The name of the sound.
   * @param mesh The mesh to attach the sound to.
   * @param minDistance The minimum distance of the sound.
   * @param maxDistance The maximum distance of the sound.
   * @returns The created sound.
   */
  async spatial(name: string, mesh: AbstractMesh, minDistance: number = 0, maxDistance: number = 30): Promise<StaticSound> {
    if (this.game.config.mute || !this.game.user.settings.enableSound) {
      return;
    }
    const sound = this.getSound(name);
    if (!sound) {
      console.warn('Spatial Sound ' + name + ' not found! Please call create() first.');
      return;
    }

    // Create a unique clone for this mesh so multiple enemies can play simultaneously
    const cloneName = `${name}_${mesh.uniqueId}`;
    const clone = await CreateSoundAsync(cloneName, sound.buffer, {
      spatialEnabled: true,
      volume: sound.volume,
      loop: sound.loop,
      playbackRate: sound.playbackRate,
      pitch: sound.pitch,
    });
    clone.spatial.attach(mesh);
    clone.spatial.distanceModel = 'linear';
    clone.spatial.minDistance = minDistance;
    clone.spatial.maxDistance = maxDistance;
    this.sounds[cloneName] = clone;

    return clone;
  }

  getSound(name: string): StaticSound {
    return this.sounds[name];
  }

  async clone(name: string, newname: string, options?: Partial<IStaticSoundOptions>): Promise<StaticSound> {
    const sound = this.getSound(name);

    if (!sound) {
      console.warn('Sound ' + name + ' not found!');
      return;
    }

    const clone = await CreateSoundAsync(newname, sound.buffer, options);
    this.sounds[newname] = clone;
    return clone;
  }

  play(name: string, mesh?: AbstractMesh, options?: Partial<IStaticSoundPlayOptions>, force: boolean = false): StaticSound {
    if (this.game.config.mute || !this.game.user.settings.enableSound) {
      return;
    }
    const soundName = mesh ? name + '_' + mesh.uniqueId : name;
    let sound = this.getSound(soundName);

    // Fallback: if spatial clone not yet registered, use base sound
    if (!sound && mesh) {
      sound = this.getSound(name);
    }

    if (!sound) {
      console.warn('Sound ' + soundName + ' not found!');
      return;
    }

    if (!force && (sound.state === SoundState.Started || sound.state === SoundState.Starting)) {
      return;
    }
    sound.play(options);
    return sound;
  }

  stop(name: string, mesh?: AbstractMesh, options?: Partial<IStaticSoundStopOptions>): void {
    const sound = mesh ? this.getSound(name + '_' + mesh.uniqueId) : this.getSound(name);

    if (!sound) {
      console.warn('Sound ' + name + ' not found!');
      return;
    }

    if (sound.state === SoundState.Started || sound.state === SoundState.Starting) {
      sound.stop(options);
    }
  }

  fadeOut(name: string, duration: number = 1): void {
    const sound = this.getSound(name);

    if (!sound) {
      console.warn('Sound ' + name + ' not found!');
      return;
    }

    sound.setVolume(0, { duration: duration, shape: AudioParameterRampShape.Logarithmic });

    setTimeout(() => {
      sound.stop();
    }, duration * 1000);
  }

  /**
   * Stops and disposes all spatial sound clones.
   * Spatial clones are identified by keys containing '_' followed by a mesh uniqueId.
   * Base sounds (without underscore suffix) are preserved for reuse.
   */
  stopAllSpatial(): void {
    const keysToRemove: string[] = [];
    for (const [key, sound] of Object.entries(this.sounds)) {
      // Spatial clones have format: baseName_meshUniqueId (e.g. 'explosion_15092')
      const underscoreIndex = key.lastIndexOf('_');
      if (underscoreIndex > 0) {
        const suffix = key.substring(underscoreIndex + 1);
        // Check if suffix is a number (mesh uniqueId)
        if (/^\d+$/.test(suffix)) {
          if (sound.state === SoundState.Started || sound.state === SoundState.Starting) {
            sound.stop();
          }
          sound.dispose();
          keysToRemove.push(key);
        }
      }
    }
    for (const key of keysToRemove) {
      delete this.sounds[key];
    }
  }

  /**
   * Lets the browser speak the given text, if the browser supports it.
   *
   * @param text can be a translation key or a string
   */
  async textToSpeech(text: string) {
    if (!this.game.user.settings.enableSpeech) {
      return;
    }
    if ('speechSynthesis' in window) {
      const voices = await this.voicesReady;
      const msg = new SpeechSynthesisUtterance();
      msg.lang = 'en-GB';
      msg.text = text;
      msg.volume = this.game.user.settings.volumeSpeech;
      //msg.rate = 1;
      //msg.pitch = 1;
      msg.voice = this.voice;
      window.speechSynthesis.speak(msg);
    } else {
      console.warn('Browser does not support text to speech!');
    }
  }

  speak(text: string, delay: number = 0): NodeJS.Timeout {
    if (!this.game.user.settings.enableSpeech) {
      return;
    }
    return setTimeout(() => {
      this.textToSpeech(text);
    }, delay);
  }

  /**
   * Converts the given word to a soundex code.
   * Soundex is a phonetic algorithm for indexing names by sound, as pronounced in English.
   * The goal is for homophones to be encoded to the same representation so that they can be matched despite minor differences in spelling.
   *
   * @param word to convert
   * @returns soundex code
   */
  soundex(word: string): string {
    const getCode = (letter: string) => {
      return SoundService.soundexDic[letter.toUpperCase()];
    };
    let initialCharacter = word[0].toUpperCase();
    let initialCode = getCode(initialCharacter);
    for (let i = 1; i < word.length; ++i) {
      const letterCode = getCode(word[i]);
      if (letterCode && letterCode != initialCode) {
        initialCharacter += letterCode;
        if (initialCharacter.length == 4) break;
      }
      initialCode = letterCode;
    }
    return initialCharacter.concat('0'.repeat(4 - initialCharacter.length));
  }

  /**
   * Lets the browser listen to the users speech and returns the recognized text.
   *
   * @returns recognized text
   */
  speechToText(): Promise<string> {
    return new Promise((resolve, reject) => {
      const recognition = new ((window as any).SpeechRecognition ||
        (window as any).webkitSpeechRecognition ||
        (window as any).mozSpeechRecognition ||
        (window as any).msSpeechRecognition)();
      recognition.lang = 'en';

      recognition.onstart = () => {
        this.play('info');
      };

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        resolve(transcript);
      };

      recognition.onend = () => {
        this.play('click');
      };

      recognition.start();
    });
  }

  dispose() {
    for (const sound of Object.values(this.sounds)) {
      sound.dispose();
    }
    this.sounds = {};
  }
}
